import { useEffect, useMemo, useState } from 'react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import type { RouteDefinition } from '@/routes';
import { applyRouteMeta } from '@/lib/meta';
import { AppLayout } from '@/layout/AppLayout';
import { AssumptionPanel } from '@/components/calculator/AssumptionPanel';
import { CalculatorSeoArticle } from '@/components/calculator/CalculatorSeoArticle';
import { ChartCard } from '@/components/calculator/ChartCard';
import { FieldExplainerGuide } from '@/components/calculator/FieldExplainerGuide';
import { NumberField } from '@/components/calculator/NumberField';
import { ResultSummaryCard } from '@/components/calculator/ResultSummaryCard';
import { ShareExportRow } from '@/components/calculator/ShareExportRow';
import { RouteNextSteps } from '@/components/content/RouteNextSteps';
import { ChartContainer, ChartLegendPills, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { getMortgageFieldExplainers } from '@/content/field-explainers/mortgage';
import type { FieldExplainerContent } from '@/content/field-explainers/types';
import { calculateMortgage, type MortgageInput } from '@/engines/legacy-engines';
import {
  copyToClipboard,
  createContractShareUrl,
  exportPdfReport,
  exportRowsToCsv,
  formatCurrency,
  parseContractState
} from './shared';

type Props = { route: RouteDefinition };

const defaults: MortgageInput = {
  homePrice: 450000,
  downPayment: 90000,
  interestRate: 6.5,
  loanTermYears: 30,
  propertyTax: 5400,
  homeInsurance: 1800,
  pmi: 120,
  hoa: 0,
  extraPayment: 200
};

const chartConfig = {
  balance: {
    label: 'Ending balance',
    color: 'var(--chart-1)'
  },
  principal: {
    label: 'Principal paid',
    color: 'var(--chart-2)'
  },
  interest: {
    label: 'Interest paid',
    color: 'var(--chart-4)'
  }
} satisfies ChartConfig;

function formatCompactCurrency(value: number): string {
  if (!Number.isFinite(value)) {
    return '$0';
  }

  if (Math.abs(value) < 1000) {
    return `$${Math.round(value)}`;
  }

  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 1
  }).format(value);
}

export function MortgageCalculatorPage({ route }: Props) {
  const { toast } = useToast();
  const [state, setState] = useState<MortgageInput>(() => parseContractState('mortgage', defaults));

  useEffect(() => {
    applyRouteMeta(route);
  }, [route]);

  const result = useMemo(() => calculateMortgage(state), [state]);
  const activeSchedule = result.amortizationScheduleWithExtra.length > 0 ? result.amortizationScheduleWithExtra : result.amortizationSchedule;

  const chartData = useMemo(
    () =>
      activeSchedule.map((row, index) => ({
        year: result.chart.labels[index] ?? row.year,
        principal: result.chart.principalData[index] ?? row.principalPaid,
        interest: result.chart.interestData[index] ?? row.interestPaid,
        balance: result.chart.balanceData[index] ?? row.endingBalance
      })),
    [activeSchedule, result.chart.balanceData, result.chart.interestData, result.chart.labels, result.chart.principalData]
  );

  const monthlySavingsFromExtra = state.extraPayment > 0 ? result.totalInterestWithoutExtra - result.totalInterest : 0;

  const explainers = useMemo(() => {
    const entries = getMortgageFieldExplainers({
      input: {
        homePrice: state.homePrice,
        downPayment: state.downPayment,
        interestRate: state.interestRate,
        loanTermYears: state.loanTermYears,
        propertyTax: state.propertyTax,
        homeInsurance: state.homeInsurance,
        pmi: state.pmi,
        hoa: state.hoa,
        extraPayment: state.extraPayment
      },
      result: {
        loanAmount: result.loanAmount,
        exactDownPaymentRatio: result.exactDownPaymentRatio,
        monthlyPI: result.monthlyPI,
        monthlyTaxInsurance: result.monthlyTaxInsurance,
        totalMonthlyPayment: result.totalMonthlyPayment,
        numberOfPayments: result.numberOfPayments,
        totalInterest: result.totalInterest,
        totalInterestWithoutExtra: result.totalInterestWithoutExtra,
        interestSaved: result.interestSaved,
        actualLoanTerm: result.actualLoanTerm,
        payoffYear: result.payoffYear
      }
    });
    const byId = new Map<string, FieldExplainerContent>(entries.map((entry) => [entry.id, entry]));
    return { entries, byId };
  }, [state, result]);

  return (
    <AppLayout>
      <section className="hero-shell rounded-2xl border border-border p-6 md:p-8">
        <h1 className="font-display text-4xl leading-tight md:text-5xl">Mortgage Calculator</h1>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground md:text-base">
          Analyze monthly payment, total borrowing cost, and full amortization with optional extra principal payments.
        </p>
      </section>

      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="before" />

      <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <AssumptionPanel title="Loan assumptions" description="Configure home price, loan details, escrow costs, and optional extra principal payment.">
          <div className="grid gap-4 md:grid-cols-2">
            <NumberField id="mortgageHomePrice" label="Home price" value={state.homePrice} min={0} step={1000} prefix="$" explainer={explainers.byId.get('homePrice')} onChange={(value) => setState((prev) => ({ ...prev, homePrice: value }))} />
            <NumberField id="mortgageDownPayment" label="Down payment" value={state.downPayment} min={0} step={1000} prefix="$" explainer={explainers.byId.get('downPayment')} onChange={(value) => setState((prev) => ({ ...prev, downPayment: value }))} />
            <NumberField id="mortgageRate" label="Interest rate" value={state.interestRate} min={0} max={15} step={0.1} suffix="%" explainer={explainers.byId.get('interestRate')} onChange={(value) => setState((prev) => ({ ...prev, interestRate: value }))} />
            <NumberField id="mortgageTerm" label="Loan term (years)" value={state.loanTermYears} min={5} max={40} step={1} parseMode="integer" explainer={explainers.byId.get('loanTermYears')} onChange={(value) => setState((prev) => ({ ...prev, loanTermYears: Math.round(value) }))} />
            <NumberField id="mortgageTax" label="Annual property tax" value={state.propertyTax} min={0} step={100} prefix="$" explainer={explainers.byId.get('propertyTax')} onChange={(value) => setState((prev) => ({ ...prev, propertyTax: value }))} />
            <NumberField id="mortgageInsurance" label="Annual home insurance" value={state.homeInsurance} min={0} step={100} prefix="$" explainer={explainers.byId.get('homeInsurance')} onChange={(value) => setState((prev) => ({ ...prev, homeInsurance: value }))} />
            <NumberField id="mortgagePmi" label="Monthly PMI" value={state.pmi} min={0} step={10} prefix="$" explainer={explainers.byId.get('pmi')} onChange={(value) => setState((prev) => ({ ...prev, pmi: value }))} />
            <NumberField id="mortgageHoa" label="Monthly HOA" value={state.hoa} min={0} step={10} prefix="$" explainer={explainers.byId.get('hoa')} onChange={(value) => setState((prev) => ({ ...prev, hoa: value }))} />
            <NumberField id="mortgageExtra" label="Extra monthly principal" value={state.extraPayment} min={0} step={25} prefix="$" explainer={explainers.byId.get('extraPayment')} onChange={(value) => setState((prev) => ({ ...prev, extraPayment: value }))} />
          </div>
        </AssumptionPanel>

        <div className="space-y-4">
          <ResultSummaryCard label="Monthly principal + interest" value={formatCurrency(result.monthlyPI)} note="base amortized payment" trend="flat" explainer={explainers.byId.get('monthlyPI')} />
          <ResultSummaryCard label="Total monthly housing payment" value={formatCurrency(result.totalMonthlyPayment)} note="includes taxes, insurance, PMI/HOA" trend="flat" explainer={explainers.byId.get('totalMonthlyPayment')} />
          <ResultSummaryCard label="Total interest" value={formatCurrency(result.totalInterest)} note={`${formatCurrency(monthlySavingsFromExtra)} saved with extra payment`} trend={monthlySavingsFromExtra > 0 ? 'up' : 'flat'} explainer={explainers.byId.get('totalInterest')} />
          <ResultSummaryCard label="Projected payoff year" value={`${result.payoffYear}`} note={`${result.actualLoanTerm} years estimated term`} trend="up" explainer={explainers.byId.get('payoffYear')} />

          <ShareExportRow
            onCopyShare={async () => {
              const shareUrl = createContractShareUrl(route.canonicalPath, 'mortgage', state as Record<string, unknown>);
              const ok = await copyToClipboard(shareUrl);
              toast({ title: ok ? 'Share link copied' : 'Share link ready', description: ok ? shareUrl : `Copy manually: ${shareUrl}`, variant: ok ? 'success' : 'warning' });
            }}
            onExportCsv={() => {
              const headers = ['Year', 'Principal Paid', 'Interest Paid', 'Ending Balance', 'Equity %'];
              const rows = activeSchedule.map((row) => [row.year, Math.round(row.principalPaid), Math.round(row.interestPaid), Math.round(row.endingBalance), row.equityPercent]);
              exportRowsToCsv(headers, rows, 'mortgage-amortization.csv');
              toast({ title: 'CSV exported', description: 'mortgage-amortization.csv downloaded.', variant: 'success' });
            }}
            onExportPdf={async () => {
              const ok = await exportPdfReport({
                title: 'Mortgage Amortization',
                subtitle: `${state.loanTermYears}-year loan`,
                summaryLeft: [
                  `Home price: ${formatCurrency(state.homePrice)}`,
                  `Down payment: ${formatCurrency(state.downPayment)}`,
                  `Interest rate: ${state.interestRate.toFixed(2)}%`,
                  `Monthly P&I: ${formatCurrency(result.monthlyPI)}`
                ],
                summaryRight: [
                  `Total monthly payment: ${formatCurrency(result.totalMonthlyPayment)}`,
                  `Total interest: ${formatCurrency(result.totalInterest)}`,
                  `Interest saved: ${formatCurrency(monthlySavingsFromExtra)}`,
                  `Projected payoff year: ${result.payoffYear}`
                ],
                headers: ['Year', 'Principal', 'Interest', 'Balance', 'Equity %'],
                rows: activeSchedule.map((row) => [
                  row.year,
                  formatCurrency(row.principalPaid),
                  formatCurrency(row.interestPaid),
                  formatCurrency(row.endingBalance),
                  `${row.equityPercent}%`
                ]),
                filename: 'mortgage-amortization.pdf'
              });

              toast({
                title: ok ? 'PDF exported' : 'PDF export unavailable',
                description: ok ? 'mortgage-amortization.pdf downloaded.' : 'PDF dependencies are not available right now.',
                variant: ok ? 'success' : 'warning'
              });
            }}
          />
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <ChartCard
          title="Amortization curves"
          description="Ending balance, yearly principal paid, and yearly interest paid."
          legend={<ChartLegendPills config={chartConfig} keys={['balance', 'principal', 'interest']} />}
        >
          <ChartContainer config={chartConfig} family="comparison">
            <LineChart
              accessibilityLayer
              data={chartData}
              margin={{
                top: 18,
                left: 12,
                right: 12
              }}
            >
              <CartesianGrid vertical={false} />
              <XAxis dataKey="year" tickLine={false} axisLine={false} tickMargin={8} />
              <YAxis tickLine={false} axisLine={false} tickMargin={8} width={70} tickFormatter={(value) => formatCompactCurrency(Number(value))} />
              <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" formatter={(value) => formatCurrency(Number(value ?? 0))} />} />
              <Line dataKey="balance" type="natural" stroke="var(--color-balance)" strokeWidth={3.1} dot={false} />
              <Line dataKey="principal" type="natural" stroke="var(--color-principal)" strokeWidth={1.9} dot={false} />
              <Line dataKey="interest" type="natural" stroke="var(--color-interest)" strokeWidth={1.9} strokeDasharray="5 4" dot={false} />
            </LineChart>
          </ChartContainer>
        </ChartCard>

        <ChartCard title="Amortization table" description="Complete yearly schedule from the active payoff scenario.">
          <Table containerClassName="max-h-[28rem]" stickyHeader compact>
            <TableHeader>
              <TableRow>
                <TableHead numeric>Year</TableHead>
                <TableHead numeric>Principal</TableHead>
                <TableHead numeric>Interest</TableHead>
                <TableHead numeric>Balance</TableHead>
                <TableHead numeric>Equity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeSchedule.map((row) => (
                <TableRow key={row.year}>
                  <TableCell numeric>{row.year}</TableCell>
                  <TableCell numeric>{formatCurrency(row.principalPaid)}</TableCell>
                  <TableCell numeric>{formatCurrency(row.interestPaid)}</TableCell>
                  <TableCell numeric>{formatCurrency(row.endingBalance)}</TableCell>
                  <TableCell numeric>{row.equityPercent}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ChartCard>
      </section>

      <section>
        <FieldExplainerGuide
          title="How each number is calculated"
          description="Every input and result on this page, explained with your numbers filled in. Tap the info icon next to any field for the same explanation in place."
          entries={explainers.entries}
        />
      </section>

      <RouteNextSteps canonicalPath={route.canonicalPath} />
      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="after" />
    </AppLayout>
  );
}
